/**
 * Employee Details / Field History breadcrumb origins. Built on the generic `from`-origin
 * helpers in `breadcrumbOrigin.ts`; this module owns the details-specific matcher table and
 * emits the local `Page` shape consumed by `FormPageTemplate`.
 *
 * The Details page roots its breadcrumb on where the user came from: another employee's
 * dashboard ("{Employee}'s Dashboard", resolved by `useEmployeeDashboardOrigin`) or a
 * recognized list page. Field History extends the chain one level deeper, keeping the
 * "Employee Details" middle crumb pointing back with the origin intact.
 */
import type { Page } from "../components/Breadcrumbs";

import toEmployeeUrl, { employeeNameLink } from "./URLs/Employee";
import {
	type OriginMatcher,
	appendCurrentLocationAsFrom,
	getSanitizedFrom,
	matchOrigin,
	nestedFromChain,
} from "./breadcrumbOrigin";

export { appendCurrentLocationAsFrom } from "./breadcrumbOrigin";

const EMPLOYEE_DETAILS_PATH_PREFIX = "/details/";

type DetailsOriginMatcher = OriginMatcher & {
	/** Only consider this matcher when the origin path starts with this prefix (guards generic view-suffix matches). */
	requirePrefix?: string;
};

// Order matters: most-specific first.
const ORIGIN_MATCHERS: readonly DetailsOriginMatcher[] = [
	// company info
	{ match: "/company/meetings/coverage", label: "Meeting Coverage" },
	{ match: "/company/meetings", label: "Company Meetings" },
	// competencies (team page lives under /employees/:id/ but is a list page, not a details page)
	{ match: "/competencies/team", label: "My Team Competencies" },
	{ match: "/company/competencies", label: "All Competencies" },
	{ match: "tab=org-chart", label: "Org Chart" },
	{ match: "/org-chart", label: "Org Chart" },
	{ match: "/directory", label: "Directory" },
	// admin (specific pages before the /administration/ catch-all)
	{ match: "/administration/roles/", label: "Role Details" },
	{ match: "/administration/compensation", label: "Compensation" },
	{ match: "/super-user-admin/compensation/", label: "Compensation" },
	{ match: "/administration/", label: "Company Settings" },
	// goals ("/goals/teamGoals" contains "/team" — keep above the feedback view matchers)
	{ match: "/goals/mygoals", label: "My Goals" },
	{ match: "/goals/teamGoals", label: "My Team's Goals" },
	{ match: "/company/goals/list", label: "Company Goals" },
	{ match: "/company/goals/dashboard", label: "Company Goals Overview" },
	{ match: "/company/goals/alignment", label: "Company Goals Alignment" },
	{ match: "/company/goals/employees-without-goals", label: "Employees Without Goals" },
	// reviews
	{ match: "/forms-i-need-to-do", label: "Forms I Need To Do" },
	{ match: "/forms-i-did", label: "Forms I Did" },
	{ match: "/reviews-of-my-team", label: "My Team's Reviews" },
	{ match: "/reviews-of-me", label: "Reviews of Me" },
	{ match: "/company/reviews/cycles", label: "Review Cycles" },
	{ match: "/review-cycle/", label: "Review Cycles" },
	{ match: "/company/reviews/all", label: "Company Reviews" },
	{ match: "/company/reviews/form-report", label: "Company Forms" },
	{ match: "/company/reviews/overview", label: "Review Assignments" },
	{ match: "/company/reviews/graphs", label: "Performance Graphs" },
	// feedback (the view segments are generic substrings — guard on the /feedback/{userId}/ prefix)
	{ match: "/company/feedback", label: "Company Feedback" },
	{ match: "/received", label: "My Feedback", requirePrefix: "/feedback/" },
	{ match: "/given", label: "Feedback I've Given", requirePrefix: "/feedback/" },
	{ match: "/team", label: "My Team's Feedback", requirePrefix: "/feedback/" },
	{ match: "/notes", label: "My Notes", requirePrefix: "/feedback/" },
	{ match: "/requests", label: "Incoming Feedback Requests", requirePrefix: "/feedback/" },
	{ match: "/outgoing", label: "Outgoing Feedback Requests", requirePrefix: "/feedback/" },
	// meetings (the action-items view is the same path plus a tab query param)
	{ match: "tab=action-items", label: "My Action Items" },
	{ match: "/meeting-workspaces", label: "My Meeting Workspaces" },
	// workflows
	{ match: "/company/workflows/todos-report", label: "Company To-Do's" },
	{ match: "/company/workflows", label: "Company Workflows" },
	// resources/documents
	{ match: "/company/resources/documents", label: "Company Documents" },
	{ match: "/company/resources/links", label: "Company Links" },
	{ match: "/documents/", label: "My Documents" },
	// engagement
	{ match: "/engagement/overview", label: "Engagement Overview" },
	{ match: "/engagement/cohorts", label: "Engagement Cohorts" },
	{ match: "/engagement/factors", label: "Engagement Factors" },
	{ match: "/engagement/questions", label: "Engagement Questions" },
	{ match: "/engagement/enps", label: "Engagement eNPS" },
	// surveys / compensation / analytics
	{ match: "/surveys/my-surveys", label: "My Surveys" },
	{ match: "/surveys", label: "All Surveys" },
	{ match: "/compensation", label: "Compensation" },
	{ match: "/analytics", label: "Reporting & Analytics" },
];

const matchDetailsOrigin = (path: string): DetailsOriginMatcher | null =>
	matchOrigin(
		path,
		ORIGIN_MATCHERS.filter(({ requirePrefix }) => !requirePrefix || path.startsWith(requirePrefix))
	);

export type EmployeeDetailsListOrigin = {
	/** Sanitized internal `from` path back to the originating list page. */
	from: string;
	/** Root-crumb label for that list page. */
	label: string;
};

/**
 * Match a `from` against the list-origin table, unwrapping details-page values first so a
 * details → details chain keeps the original list origin. Never matches a details URL itself,
 * whose query could contain a matcher substring.
 */
const resolveListOriginThroughDetails = (from: string): EmployeeDetailsListOrigin | null => {
	const isDetailsPage = (value: string): boolean => value.startsWith(EMPLOYEE_DETAILS_PATH_PREFIX);
	let origin = isDetailsPage(from) ? "" : from;
	for (const hop of nestedFromChain(from)) {
		if (!isDetailsPage(hop)) {
			origin = hop;
			break;
		}
	}
	if (!origin) return null;
	const matcher = matchDetailsOrigin(origin);
	return matcher ? { from: origin, label: matcher.label } : null;
};

/** Resolve a recognized list-page origin from the Details page's search string. */
export const parseEmployeeDetailsListOrigin = (search: string): EmployeeDetailsListOrigin | null =>
	resolveListOriginThroughDetails(getSanitizedFrom(search));

/**
 * Field History variant: the outer `from` is the Details URL, whose own query may carry the
 * list origin (list → details → field history). The outer `from` must point at the details
 * page so a crafted URL can't smuggle in an origin.
 */
export const parseEmployeeDetailsListOriginDeep = (search: string): EmployeeDetailsListOrigin | null => {
	const outer = getSanitizedFrom(search);
	if (!outer.startsWith(EMPLOYEE_DETAILS_PATH_PREFIX)) return null;
	return resolveListOriginThroughDetails(outer);
};

/**
 * `employeeNameLink` plus the current location as `from`. Self links resolve to the dashboard
 * ("/") and are left undecorated.
 */
export const employeeDetailsLinkWithFrom = (employeeId?: string, currentUserId?: string): string => {
	const href = employeeNameLink(employeeId, currentUserId);
	return href.startsWith(EMPLOYEE_DETAILS_PATH_PREFIX) ? appendCurrentLocationAsFrom(href) : href;
};

/** `EmployeeURL` plus the current location as `from` (unlike `employeeDetailsLinkWithFrom`, self links stay on `/details/`). */
export const employeeUrlWithFrom = (employeeId?: string): string =>
	appendCurrentLocationAsFrom(toEmployeeUrl(employeeId));

/** Structural subset of `EmployeeDashboardOriginResult` from `useEmployeeDashboardOrigin`. */
type DashboardOriginArg =
	| { status: "none" }
	| { status: "loading" }
	| { status: "resolved"; from: string; employeeName: string };

const resolveRootCrumb = (dashboardOrigin: DashboardOriginArg, listOrigin: EmployeeDetailsListOrigin | null): Page => {
	if (dashboardOrigin.status === "resolved") {
		return {
			pageID: "dashboard",
			label: `${dashboardOrigin.employeeName}'s Dashboard`,
			url: dashboardOrigin.from,
		};
	}
	if (listOrigin) {
		return { pageID: "breadcrumb-employee-details-origin", label: listOrigin.label, url: listOrigin.from };
	}
	return { pageID: "dashboard", label: "Dashboard", url: "/" };
};

type BuildEmployeeDetailsBreadcrumbsArgs = {
	/** The Details page's `location.search`. */
	search: string;
	/** Result of `useEmployeeDashboardOrigin()` (deep: false). */
	dashboardOrigin: DashboardOriginArg;
	/** The employee whose details are being viewed. */
	userId: string;
};

/**
 * Build the Details page crumbs: `{origin} > Employee Details`. Returns [] while a dashboard
 * origin's employee name is still resolving so the root crumb doesn't flash a generic label.
 */
export const buildEmployeeDetailsBreadcrumbs = ({
	search,
	dashboardOrigin,
	userId,
}: BuildEmployeeDetailsBreadcrumbsArgs): Page[] => {
	if (dashboardOrigin.status === "loading") return [];
	const root = resolveRootCrumb(dashboardOrigin, parseEmployeeDetailsListOrigin(search));
	return [root, { pageID: "employee-details", label: "Employee Details", url: `/details/${userId}` }];
};

type BuildFieldHistoryBreadcrumbsArgs = BuildEmployeeDetailsBreadcrumbsArgs & {
	fieldId: string;
	fieldName: string;
};

/**
 * Build the Field History crumbs: `{origin} > Employee Details > History of {field}`. The
 * middle crumb links back to the Details URL carried in `from` (origin intact), falling back
 * to the plain details path.
 */
export const buildFieldHistoryBreadcrumbs = ({
	search,
	dashboardOrigin,
	userId,
	fieldId,
	fieldName,
}: BuildFieldHistoryBreadcrumbsArgs): Page[] => {
	if (dashboardOrigin.status === "loading") return [];
	const root = resolveRootCrumb(dashboardOrigin, parseEmployeeDetailsListOriginDeep(search));
	// For every recognized deep origin the outer `from` is the Details URL (origin intact);
	// a plain details `from` also passes through so query state like `editing=true` survives.
	const outerFrom = getSanitizedFrom(search);
	const detailsUrl = outerFrom.startsWith(EMPLOYEE_DETAILS_PATH_PREFIX) ? outerFrom : `/details/${userId}`;
	return [
		root,
		{ pageID: "employee-details", label: "Employee Details", url: detailsUrl },
		{ pageID: "view-field-history", label: `History of ${fieldName}`, url: `/view-field-history/${fieldId}` },
	];
};
